
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles, BookText, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AISermonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: () => void;
}

export function AISermonDialog({
  open,
  onOpenChange,
  onGenerate,
}: AISermonDialogProps) {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [scripture, setScripture] = useState("");
  const [theme, setTheme] = useState("");

  const handleGenerate = () => {
    setLoading(true);
    // We'll call onGenerate directly without actually making the AI call yet
    // until the user confirms they're ready
    setTimeout(() => {
      setLoading(false);
      onGenerate();
    }, 1000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center text-xl">
            <Sparkles className="mr-2 h-5 w-5 text-purple-500" />
            Create AI Sermon Plan
          </DialogTitle>
        </DialogHeader>

        <div className="px-6">
          <div className="bg-purple-50 border border-purple-100 rounded-md p-4 mb-4">
            <h3 className="text-sm font-medium text-purple-800 flex items-center mb-2">
              <BookText className="mr-2 h-4 w-4" />
              AI Sermon Assistant
            </h3>
            <p className="text-xs text-purple-700 mb-0">
              Enter optional information to guide your sermon creation. You can provide as much or as little as you'd like.
            </p>
          </div>
        </div>

        <ScrollArea className="flex-1 px-6 overflow-auto max-h-[60vh]">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Sermon Title (Optional)</Label>
              <Input
                id="title"
                placeholder="e.g., 'Finding Peace in Troubled Times'"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="text-xs text-gray-500">Leave blank for AI to suggest a title</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="scripture">Scripture Reference (Optional)</Label>
              <Input
                id="scripture"
                placeholder="e.g., John 14:27 or Psalm 23"
                value={scripture}
                onChange={(e) => setScripture(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="theme">Theme or Focus (Optional)</Label>
              <Textarea
                id="theme"
                placeholder="e.g., Peace, Faith, Love, Forgiveness, or a specific topic"
                className="h-20 resize-none"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 mt-auto border-t flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Sermon
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
